/**
 * Public memorial — Memory Soundtrack section (featured song, favourites, voice memories).
 */
import React from 'react';
import { Mic, Music } from 'lucide-react';
import { MemorialSoundtrackPlayer } from './MemorialSoundtrackPlayer';
import { MusicLinkCard } from './MusicLinkCard';
import { MEMORIAL_COPY } from '../../utils/memorial';
import { getFirstStoryAudioUrl } from '../../utils/storyDisplay';
import { getBackendBaseUrl } from '../../utils/apiBase';

const BACKEND = getBackendBaseUrl();

function VoiceMemoryItem({ memory }) {
  const audioUrl = getFirstStoryAudioUrl(memory, BACKEND);
  return (
    <li className="rounded-xl border border-[hsl(38_14%_84%)] bg-white/90 px-4 py-3 text-sm">
      <p className="font-medium text-[hsl(24_14%_16%)]">{memory.title}</p>
      {memory.excerpt ? (
        <p className="mt-1 text-xs text-[hsl(32_10%_42%)] line-clamp-2">{memory.excerpt}</p>
      ) : null}
      {audioUrl ? (
        <audio controls preload="none" className="mt-3 w-full max-w-md" src={audioUrl}>
          <track kind="captions" />
        </audio>
      ) : null}
    </li>
  );
}

function OwnerEmptyNote({ children }) {
  return <p className="memorial-public__empty-note">{children}</p>;
}

export function MemorySoundtrackSection({
  music = null,
  soundtrack = null,
  voiceMemories = [],
  showVoice = true,
  showSoundtrack = true,
  ownerPreview = false,
}) {
  const featured = music?.featured || (soundtrack?.url ? {
    title: soundtrack.title,
    artist: soundtrack.artist,
    link_url: soundtrack.url,
    note: soundtrack.note,
    platform: soundtrack.platform,
    embeddable: soundtrack.embeddable,
    embed_url: soundtrack.embed_url,
    playback: soundtrack.playback || 'audio',
  } : null);

  const otherTracks = music?.tracks || [];
  const hasFeatured = Boolean(featured?.link_url);
  const hasTracks = otherTracks.length > 0;
  const hasVoice = showVoice && voiceMemories.length > 0;
  const hasAny = hasFeatured || hasTracks || hasVoice;

  if (!showSoundtrack && !showVoice) return null;
  if (!hasAny && !ownerPreview) return null;

  return (
    <section className="space-y-6" data-testid="memory-soundtrack-section">
      <div>
        <h2 className="memorial-public__section-title flex items-center gap-2">
          <Music className="h-3.5 w-3.5" />
          Memory Soundtrack
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[hsl(32_10%_42%)]">
          Songs, voice memories and moments chosen by the family.
        </p>
      </div>

      {showSoundtrack && hasFeatured ? (
        <MemorialSoundtrackPlayer track={featured} featured />
      ) : showSoundtrack && ownerPreview && !hasFeatured ? (
        <div className="memorial-public__empty">
          <p>No featured soundtrack yet.</p>
          <OwnerEmptyNote>Add song links in the memorial workspace and choose one as featured.</OwnerEmptyNote>
        </div>
      ) : null}

      {showSoundtrack && hasTracks ? (
        <div className="space-y-3">
          <h3 className="font-serif text-base text-[hsl(24_14%_18%)]">Songs they loved</h3>
          <ul className="space-y-3">
            {otherTracks.map((track) => (
              <li key={track.id || track.link_url}>
                <MusicLinkCard track={track} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {showVoice && hasVoice ? (
        <div className="memorial-public__card p-6 sm:p-8">
          <h3 className="flex items-center gap-2 font-serif text-base text-[hsl(24_14%_18%)]">
            <Mic className="h-4 w-4 text-[#8B7355]/80" />
            {MEMORIAL_COPY.audioKeepsake}
          </h3>
          <ul className="mt-4 space-y-3">
            {voiceMemories.map((m) => (
              <VoiceMemoryItem key={m.id} memory={m} />
            ))}
          </ul>
        </div>
      ) : showVoice && ownerPreview && !hasVoice ? (
        <div className="memorial-public__empty">
          <p>No public voice memories yet.</p>
          <OwnerEmptyNote>Choose voice keepsakes in the memorial workspace.</OwnerEmptyNote>
        </div>
      ) : null}
    </section>
  );
}

export default MemorySoundtrackSection;
